import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Trans, useTranslation } from "react-i18next";
import {
  UIButton,
  UIForm,
  UIInput,
  UIMenuItem,
  UISelect,
  UISpin,
  Delete,
  HelpOutline,
} from "ui";
import { useAuth } from "../../hooks/UseAuth";
import { useSignUp } from "../../hooks/UseSignUp.hook";
import { COUNTRIES, ROUTE_URLS } from "../../constants/consts";
import {
  clearLocalStorage,
  clearTempLanguage,
  getSession,
  getUserId,
  getUserLanguage,
  setUserLanguage,
} from "../../api/sessionManagement";
import {
  getCompanyData,
  confirmRegisteredUser,
  userCheckRegNumber,
  userSignUpSecond,
} from "../../api/userRegistration";
import { checkSession } from "../../api/sessionCheck";
import { updateUserLanguageCache } from "../../hooks/useUserLanguage.hook";
import "./SignUp.sass";

export const SignUp = () => {
  const { handleSignUp } = useSignUp();
  const { i18n, ready, t } = useTranslation();
  const navigate = useNavigate();

  const {
    userPortalEmail,
    userName,
    email,
    password,
    zip,
    city,
    address,
    boards,
    owners,
    region,
    companyName,
    vat,
    phone,
    registration,
    handleChangeZip,
    handleChangeBoard,
    handleAddBoard,
    handleRemoveBoard,
    handleChangeCity,
    handleChangeAddress,
    handleChangeOwner,
    handleAddOwner,
    handleRemoveOwner,
    handleChangeEmail,
    handleChangeRegion,
    handleChangeCompanyName,
    handleChangeVat,
    handleChangePhone,
    handleChangeRegistration,
    handleChangeUserPortalEmail,
    handleChangeUserName,
    clearFormData,
    setFormDataFromServer,
  } = useAuth({ handleClick: handleSignUp });

  const [isSecondStep, setIsSecondStep] = useState<boolean>(() => {
    const savedStep = localStorage.getItem("isSecondStep");
    return savedStep ? JSON.parse(savedStep) : false;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateConflictFields, setDuplicateConflictFields] = useState({
    registration: false,
    vat: false,
  });
  const [firstStepSubmitAttempted, setFirstStepSubmitAttempted] = useState(false);
  const [secondStepSubmitAttempted, setSecondStepSubmitAttempted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const optionsForAlertWithRegNumConflict = useMemo(
    () => ({
      title: t("oops"),
      icon: "error" as const,
      confirmButtonText: t("confirmButton"),
      confirmButtonColor: "#0ebfff",
    }),
    [t]
  );

  const optionsForAlertSuccess = useMemo(
    () => ({
      title: t("registrationSuccess"),
      text: t("registrationEmailMessage"),
      icon: "success" as const,
      confirmButtonText: t("confirmButton"),
      confirmButtonColor: "#0ebfff",
    }),
    [t]
  );

  useEffect(() => {
    const handleStorageChange = () => {
      const savedStep = localStorage.getItem("isSecondStep");
      if (savedStep !== null) {
        setIsSecondStep(JSON.parse(savedStep));
      }
    };

    window.addEventListener("localStorageUpdate", handleStorageChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("localStorageUpdate", handleStorageChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!isSecondStep) {
      clearFormData();
    }
  }, [clearFormData, isSecondStep]);

  useEffect(() => {
    if (!isSecondStep) {
      return;
    }

    if (boards.length === 0) {
      handleAddBoard();
    }

    if (owners.length === 0) {
      handleAddOwner();
    }
  }, [boards.length, handleAddBoard, handleAddOwner, isSecondStep, owners.length]);

  useEffect(() => {
    const syncLanguageOnSecondStep = async () => {
      if (!isSecondStep) {
        return;
      }

      const userId = getUserId();
      if (!userId) {
        return;
      }

      try {
        const response = await checkSession();
        if (!response.error && response?.data?.data?.lang) {
          const localLang = getUserLanguage(userId);
          if (localLang !== response.data.data.lang) {
            setUserLanguage(userId, response.data.data.lang);
            updateUserLanguageCache(userId, response.data.data.lang);
            await i18n.changeLanguage(response.data.data.lang);
          }
        }
      } catch (error) {
        console.error("Failed to check session language:", error);
      }
    };

    syncLanguageOnSecondStep();
  }, [i18n, isSecondStep]);

  useEffect(() => {
    if (!isSecondStep || !getSession()) {
      return;
    }

    const loadCompanyData = async () => {
      try {
        const response = await getCompanyData();
        if (!response.error && response?.data?.data) {
          const companyData = response.data.data;
          const formData: any = {};

          if (companyData.name) formData.companyName = companyData.name;
          if (companyData.email) formData.email = companyData.email;
          if (companyData.phone) formData.phone = companyData.phone;
          if (companyData.vat) formData.vat = companyData.vat;
          if (companyData.registration_number)
            formData.registration = companyData.registration_number;

          if (companyData.legal_address) {
            const legalAddress = companyData.legal_address;
            if (legalAddress.street) formData.address = legalAddress.street;
            if (legalAddress.city) formData.city = legalAddress.city;
            if (legalAddress.postal_code) formData.zip = legalAddress.postal_code;
            if (legalAddress.region) formData.region = legalAddress.region;
          }

          if (Array.isArray(companyData.boards)) {
            const boardNames = companyData.boards
              .map((board: any) => board.name || "")
              .filter((name: string) => name);
            formData.boards = boardNames.length > 0 ? boardNames : [];
          }

          if (Array.isArray(companyData.beneficial_owners)) {
            const ownerNames = companyData.beneficial_owners
              .map((owner: any) => owner.name || "")
              .filter((name: string) => name);
            formData.owners = ownerNames.length > 0 ? ownerNames : [];
          }

          setFormDataFromServer(formData);
        }
      } catch (error) {
        console.error("Failed to load company data:", error);
      }
    };

    loadCompanyData();
  }, [isSecondStep, setFormDataFromServer]);

  useEffect(() => {
    setFirstStepSubmitAttempted(false);
    setSecondStepSubmitAttempted(false);
    setValidationErrors({});
    setDuplicateConflictFields({ registration: false, vat: false });
  }, [isSecondStep]);

  const handleCheckRegNumber = async () => {
    if (!registration || !vat) {
      return { isValid: false, regNumValid: true, vatValid: true };
    }

    const result = await userCheckRegNumber(registration, vat);
    return {
      isValid: result?.data?.data?.is_valid,
      regNumValid: result?.data?.data?.is_valid_reg_num,
      vatValid: result?.data?.data?.is_valid_vat,
    };
  };

  const handleRegistrationFieldChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setDuplicateConflictFields((prev) => ({ ...prev, registration: false }));
    handleChangeRegistration(event);
  };

  const handleVatFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDuplicateConflictFields((prev) => ({ ...prev, vat: false }));
    handleChangeVat(event);
  };

  const validateEmailField = (value: string): string | undefined => {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return t("required_field");
    }

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue);
    if (!isValidEmail) {
      return t("order_delivery_info.invalid_email");
    }

    return undefined;
  };

  const validateSignUpForm = () => {
    const errors: Record<string, string> = {};

    if (isSecondStep) {
      if (!boards[0]?.trim()) {
        errors["board-0"] = t("required_field");
      }
      if (!owners[0]?.trim()) {
        errors["owner-0"] = t("required_field");
      }
      return errors;
    }

    if (!(companyName || "").trim()) errors.companyName = t("required_field");
    if (!(region || "").trim()) errors.region = t("required_field");
    if (!(registration || "").trim()) errors.registration = t("required_field");
    if (!(vat || "").trim()) errors.vat = t("required_field");
    if (!(phone || "").trim()) errors.phone = t("required_field");
    if (!(address || "").trim()) errors.address = t("required_field");
    if (!(city || "").trim()) errors.city = t("required_field");
    if (!(zip || "").trim()) errors.zip = t("required_field");
    if (!(userName || "").trim()) errors.userName = t("required_field");

    const companyEmailError = validateEmailField(email || "");
    if (companyEmailError) {
      errors.email = companyEmailError;
    }

    const portalEmailError = validateEmailField(userPortalEmail || "");
    if (portalEmailError) {
      errors.userPortalEmail = portalEmailError;
    }

    return errors;
  };

  useEffect(() => {
    const shouldValidateCurrentStep =
      (isSecondStep && secondStepSubmitAttempted) ||
      (!isSecondStep && firstStepSubmitAttempted);

    if (!shouldValidateCurrentStep) {
      return;
    }

    setValidationErrors(validateSignUpForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address,
    boards,
    city,
    companyName,
    email,
    isSecondStep,
    owners,
    phone,
    registration,
    region,
    firstStepSubmitAttempted,
    secondStepSubmitAttempted,
    t,
    userName,
    userPortalEmail,
    vat,
    zip,
  ]);

  const getInputClassName = (fieldKey: string) => {
    const hasDuplicateError =
      (fieldKey === "registration" && duplicateConflictFields.registration) ||
      (fieldKey === "vat" && duplicateConflictFields.vat);

    return `sign-up-form__input ${
      validationErrors[fieldKey] || hasDuplicateError
        ? "sign-up-form__input--error"
        : ""
    }`;
  };

  const getSelectClassName = (fieldKey: string) =>
    `sign-up-form__select ${
      validationErrors[fieldKey] ? "sign-up-form__select--error" : ""
    }`;

  const handleSubmitSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    if (isSecondStep) {
      setSecondStepSubmitAttempted(true);
    } else {
      setFirstStepSubmitAttempted(true);
    }
    const errors = validateSignUpForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsLoading(true);
    try {
      if (isSecondStep) {
        const result = await userSignUpSecond(
          userPortalEmail,
          userName,
          owners,
          boards,
          companyName,
          email,
          phone,
          vat,
          registration,
          city,
          address,
          zip,
          region
        );

        if (result) {
          const currentUserId = Number(getUserId());
          if (currentUserId) {
            await confirmRegisteredUser(currentUserId);
          }
          await Swal.fire(optionsForAlertSuccess);
          localStorage.setItem("isSecondStep", JSON.stringify(false));
          clearTempLanguage();
          clearLocalStorage();
          clearFormData();
          navigate(ROUTE_URLS.SIGN_IN);
        }

        return;
      }

      const regCheckResult = await handleCheckRegNumber();
      if (!regCheckResult.isValid) {
        let errorText = t("invalidRegNumber") || "";
        if (regCheckResult.regNumValid === false) {
          setDuplicateConflictFields({ registration: true, vat: true });
          errorText = t("regNumVatConflict") || errorText;
        } else if (regCheckResult.vatValid === false) {
          setDuplicateConflictFields({ registration: false, vat: true });
          errorText = t("vatConflict") || errorText;
        } else {
          setDuplicateConflictFields({ registration: false, vat: false });
        }
        await Swal.fire({ ...optionsForAlertWithRegNumConflict, text: errorText });
        return;
      }

      await handleSignUp(
        userPortalEmail || "",
        userName || "",
        email || "",
        password || "",
        region || "",
        companyName || "",
        vat || "",
        phone || "",
        registration || "",
        zip || "",
        city || "",
        address || "",
        owners || [],
        boards || []
      );
    } finally {
      setIsLoading(false);
    }
  };

  const stepLabels = useMemo(
    () => [
      { id: 1, label: t("company_details") },
      { id: 2, label: t("register") },
    ],
    [t]
  );

  if (!ready) {
    return (
      <div className="sign-up-loading">
        <UISpin />
      </div>
    );
  }

  return (
    <UIForm className="sign-up-form" onSubmit={handleSubmitSignUp} noValidate>
      <div className="sign-up-form__steps" aria-label="signup-steps">
        {stepLabels.map((step, index) => {
          const isActive = (isSecondStep ? 2 : 1) === step.id;
          const isCompleted = isSecondStep && step.id === 1;

          return (
            <div
              key={step.id}
              className={`sign-up-form__step ${isActive ? "is-active" : ""} ${
                isCompleted ? "is-complete" : ""
              }`}
            >
              <span className="sign-up-form__step-index">{index + 1}</span>
              <span className="sign-up-form__step-label">{step.label}</span>
            </div>
          );
        })}
      </div>

      {!isSecondStep && (
        <>
          <section className="sign-up-form__section">
            <h3 className="sign-up-form__section-title">
              <Trans i18nKey="company_details" />
            </h3>

            <div className="sign-up-form__grid">
              <div className="sign-up-form__field sign-up-form__field--full">
                <label htmlFor="companyName" className="sign-up-form__label">
                  <Trans i18nKey="companyLegalName" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="companyName"
                  required
                  value={companyName}
                  className={getInputClassName("companyName")}
                  autoComplete="organization"
                  onChange={handleChangeCompanyName}
                />
                {validationErrors.companyName ? (
                  <p className="sign-up-form__error">
                    {validationErrors.companyName}
                  </p>
                ) : null}
              </div>

              <div className="sign-up-form__field">
                <label htmlFor="region" className="sign-up-form__label">
                  <Trans i18nKey="selectCountry" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UISelect<string>
                  id="region"
                  value={region || undefined}
                  placeholder={t("selectCountry")}
                  className={getSelectClassName("region")}
                  status={validationErrors.region ? "error" : undefined}
                  onChange={(selectEvent) =>
                    handleChangeRegion({
                      target: { value: String(selectEvent.target.value) },
                    } as ChangeEvent<HTMLSelectElement>)
                  }
                >
                  {Object.entries(COUNTRIES).map(([code]) => (
                    <UIMenuItem key={code} value={code}>
                      {t(`countries.${code}`)}
                    </UIMenuItem>
                  ))}
                </UISelect>
                {validationErrors.region ? (
                  <p className="sign-up-form__error">{validationErrors.region}</p>
                ) : null}
              </div>

              <div className="sign-up-form__field">
                <label htmlFor="registration" className="sign-up-form__label">
                  <Trans i18nKey="registrationNo" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="registration"
                  required
                  value={registration}
                  className={getInputClassName("registration")}
                  onChange={handleRegistrationFieldChange}
                />
                {validationErrors.registration ? (
                  <p className="sign-up-form__error">
                    {validationErrors.registration}
                  </p>
                ) : null}
              </div>

              <div className="sign-up-form__field">
                <label htmlFor="vat" className="sign-up-form__label">
                  <Trans i18nKey="vatNo" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="vat"
                  required
                  value={vat}
                  className={getInputClassName("vat")}
                  onChange={handleVatFieldChange}
                />
                {validationErrors.vat ? (
                  <p className="sign-up-form__error">{validationErrors.vat}</p>
                ) : null}
              </div>

              <div className="sign-up-form__field">
                <label htmlFor="phone" className="sign-up-form__label">
                  <Trans i18nKey="contactPhoneNumber" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="phone"
                  type="tel"
                  required
                  value={phone}
                  className={getInputClassName("phone")}
                  autoComplete="tel"
                  onChange={handleChangePhone}
                />
                {validationErrors.phone ? (
                  <p className="sign-up-form__error">{validationErrors.phone}</p>
                ) : null}
              </div>

              <div className="sign-up-form__field">
                <label htmlFor="address" className="sign-up-form__label">
                  <Trans i18nKey="legalAddress" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="address"
                  required
                  value={address}
                  className={getInputClassName("address")}
                  autoComplete="street-address"
                  onChange={handleChangeAddress}
                />
                {validationErrors.address ? (
                  <p className="sign-up-form__error">{validationErrors.address}</p>
                ) : null}
              </div>

              <div className="sign-up-form__field">
                <label htmlFor="city" className="sign-up-form__label">
                  <Trans i18nKey="city" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="city"
                  required
                  value={city}
                  className={getInputClassName("city")}
                  autoComplete="address-level2"
                  onChange={handleChangeCity}
                />
                {validationErrors.city ? (
                  <p className="sign-up-form__error">{validationErrors.city}</p>
                ) : null}
              </div>

              <div className="sign-up-form__field">
                <label htmlFor="zip" className="sign-up-form__label">
                  <Trans i18nKey="zip" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="zip"
                  required
                  value={zip}
                  className={getInputClassName("zip")}
                  autoComplete="postal-code"
                  onChange={handleChangeZip}
                />
                {validationErrors.zip ? (
                  <p className="sign-up-form__error">{validationErrors.zip}</p>
                ) : null}
              </div>

              <div className="sign-up-form__field sign-up-form__field--full">
                <label htmlFor="companyEmail" className="sign-up-form__label">
                  <Trans i18nKey="email" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="companyEmail"
                  type="email"
                  required
                  value={email}
                  className={getInputClassName("email")}
                  autoComplete="email"
                  onChange={handleChangeEmail}
                />
                {validationErrors.email ? (
                  <p className="sign-up-form__error">{validationErrors.email}</p>
                ) : null}
              </div>
            </div>
          </section>

          <section className="sign-up-form__section">
            <h3 className="sign-up-form__section-title">
              <Trans i18nKey="user_details" />
            </h3>

            <div className="sign-up-form__grid">
              <div className="sign-up-form__field sign-up-form__field--full">
                <label htmlFor="userName" className="sign-up-form__label">
                  <Trans i18nKey="first_last_name" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="userName"
                  required
                  value={userName}
                  className={getInputClassName("userName")}
                  autoComplete="name"
                  onChange={handleChangeUserName}
                />
                {validationErrors.userName ? (
                  <p className="sign-up-form__error">
                    {validationErrors.userName}
                  </p>
                ) : null}
              </div>

              <div className="sign-up-form__field sign-up-form__field--full">
                <label htmlFor="portalEmail" className="sign-up-form__label">
                  <Trans i18nKey="email" />
                  <span className="sign-up-form__required-mark">*</span>
                </label>
                <UIInput
                  id="portalEmail"
                  type="email"
                  required
                  value={userPortalEmail}
                  className={getInputClassName("userPortalEmail")}
                  autoComplete="email"
                  onChange={handleChangeUserPortalEmail}
                />
                {validationErrors.userPortalEmail ? (
                  <p className="sign-up-form__error">
                    {validationErrors.userPortalEmail}
                  </p>
                ) : null}
              </div>
            </div>
          </section>
        </>
      )}

      {isSecondStep && (
        <section className="sign-up-form__section sign-up-form__section--single">
          <h3 className="sign-up-form__section-title">
            <Trans i18nKey="companyBoard" />
          </h3>

          <div className="sign-up-form__stack">
            {boards.map((board, index) => (
              <div key={`board-${index}`} className="sign-up-form__field">
                <label
                  htmlFor={`board-${index}`}
                  className="sign-up-form__label sign-up-form__label--inline"
                >
                  {index === 0 ? (
                    <>
                      <Trans i18nKey="companyBoard" />
                      <span className="sign-up-form__required-mark">*</span>
                      <span
                        className="sign-up-form__hint"
                        title={t("companyBoardInfo")}
                      >
                        <HelpOutline />
                      </span>
                    </>
                  ) : (
                    <Trans i18nKey="companyBoard" />
                  )}
                </label>
                <UIInput
                  id={`board-${index}`}
                  value={board}
                  required={index === 0}
                  className={getInputClassName(`board-${index}`)}
                  onChange={(changeEvent) => handleChangeBoard(index, changeEvent)}
                  suffix={
                    boards.length > 1 ? (
                      <UIButton
                        type="button"
                        variant="text"
                        className="sign-up-form__icon-button"
                        onMouseDown={(mouseEvent) => mouseEvent.preventDefault()}
                        onClick={() => handleRemoveBoard(index)}
                      >
                        <Delete />
                      </UIButton>
                    ) : null
                  }
                />
                {validationErrors[`board-${index}`] ? (
                  <p className="sign-up-form__error">
                    {validationErrors[`board-${index}`]}
                  </p>
                ) : null}
              </div>
            ))}

            <UIButton
              type="button"
              variant="outlined"
              className="sign-up-form__add-button"
              onClick={handleAddBoard}
            >
              <Trans i18nKey="addBoard" />
            </UIButton>
          </div>

          <h3 className="sign-up-form__section-title sign-up-form__section-title--spaced">
            <Trans i18nKey="beneficiaryOwner" />
          </h3>

          <div className="sign-up-form__stack">
            {owners.map((owner, index) => (
              <div key={`owner-${index}`} className="sign-up-form__field">
                <label
                  htmlFor={`owner-${index}`}
                  className="sign-up-form__label sign-up-form__label--inline"
                >
                  {index === 0 ? (
                    <>
                      <Trans i18nKey="beneficiaryOwner" />
                      <span className="sign-up-form__required-mark">*</span>
                      <span
                        className="sign-up-form__hint"
                        title={t("beneficiaryOwnerInfo")}
                      >
                        <HelpOutline />
                      </span>
                    </>
                  ) : (
                    <Trans i18nKey="beneficiaryOwner" />
                  )}
                </label>
                <UIInput
                  id={`owner-${index}`}
                  value={owner}
                  required={index === 0}
                  className={getInputClassName(`owner-${index}`)}
                  onChange={(changeEvent) => handleChangeOwner(index, changeEvent)}
                  suffix={
                    owners.length > 1 ? (
                      <UIButton
                        type="button"
                        variant="text"
                        className="sign-up-form__icon-button"
                        onMouseDown={(mouseEvent) => mouseEvent.preventDefault()}
                        onClick={() => handleRemoveOwner(index)}
                      >
                        <Delete />
                      </UIButton>
                    ) : null
                  }
                />
                {validationErrors[`owner-${index}`] ? (
                  <p className="sign-up-form__error">
                    {validationErrors[`owner-${index}`]}
                  </p>
                ) : null}
              </div>
            ))}

            <UIButton
              type="button"
              variant="outlined"
              className="sign-up-form__add-button"
              onClick={handleAddOwner}
            >
              <Trans i18nKey="addOwner" />
            </UIButton>
          </div>
        </section>
      )}

      <UIButton
        type="submit"
        variant="contained"
        fullWidth
        className="sign-up-form__submit"
        disabled={isLoading}
      >
        {isLoading ? (
          <UISpin size="small" />
        ) : (
          <Trans i18nKey={isSecondStep ? "register" : "next"} />
        )}
      </UIButton>

      <p className="sign-up-form__meta">
        <Trans i18nKey="alreadyHaveAccount" />{" "}
        <Link to={ROUTE_URLS.SIGN_IN}>
          <Trans i18nKey="login" />
        </Link>
      </p>

      <p className="sign-up-form__meta sign-up-form__meta--secondary">
        <Trans i18nKey="forgot_your_password" />{" "}
        <Link to={ROUTE_URLS.RECOVER_PASSWORD}>
          <Trans i18nKey="recover_password" />
        </Link>
      </p>
    </UIForm>
  );
};
